import axios from 'axios';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { OptionType, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { addEnrollmentExhibits, removeEnrollmentExhibits } from 'api/operations/enrollmentService';
import { getUploadExhibitZipUrl } from 'api/operations/carrierFileService';
import { uploadFiles } from 'global/utils/uploadFiles';
import { makeAutoObservable, reaction, runInAction, toJS } from 'mobx';
import FileUploadDelegate from 'store/general/fileUploadDelegate';
import ModalDelegate from 'store/general/modalDelegate';
import { UploadUrls } from 'types/carrierFileService';
import { InstalledEnrollmentType } from 'types/enrollmentService';
import { COMMON_URLS } from '@insurancemenu/imenu-components-lib/dist/utils/urls';

export interface ManageExhibitsProductLine {
  id: ProductLineId;
  exhibitUrls: string[];
}

export interface ManageExhibitsContext {
  periodId: string;
  groupName: string;
  effectiveDate: string;
  productLines: ManageExhibitsProductLine[];
}

export interface ExhibitFileRow {
  id: string;
  productLineId: ProductLineId;
  fileName: string;
  fileUrl: string;
}

export function getExhibitFileName(fileUrl: string): string {
  const decodedFileUrl = decodeURIComponent(fileUrl);
  const markerIndex = decodedFileUrl.lastIndexOf('/');
  const fileName = decodedFileUrl.slice(markerIndex + 1);

  try {
    return decodeURIComponent(fileName);
  } catch {
    return fileName;
  }
}

function toManageExhibitsProductLines(period: InstalledEnrollmentType): ManageExhibitsProductLine[] {
  return (
    period.installedProposal?.productLines.map((productLine) => ({
      id: productLine.id,
      exhibitUrls: productLine.exhibitUrls ?? [],
    })) ?? []
  );
}

interface ManageExhibitsModalDelegateParams {
  onEnrollmentPeriodUpdated: (updatedPeriod: InstalledEnrollmentType) => void;
}

export class ManageExhibitsModalDelegate {
  private readonly _modalDelegate = new ModalDelegate();
  private readonly _onEnrollmentPeriodUpdated: (updatedPeriod: InstalledEnrollmentType) => void;
  private _periodId = '';
  private _groupName = '';
  private _effectiveDate = '';
  private _productLines: ManageExhibitsProductLine[] = [];
  private _selectedProductLineId = '';
  public fileUploadDelegate = new FileUploadDelegate();

  public get isVisible(): boolean {
    return this._modalDelegate.isVisible;
  }

  public get isLoading(): boolean {
    return this._modalDelegate.isLoading;
  }

  public get periodId(): string {
    return this._periodId;
  }

  public get productLines(): ManageExhibitsProductLine[] {
    return this._productLines;
  }

  public get allExhibitUrls(): string[] {
    return this._productLines.flatMap((productLine) => productLine.exhibitUrls).filter((url) => url);
  }

  public get exhibitFiles(): ExhibitFileRow[] {
    return this._productLines.flatMap((productLine) =>
      productLine.exhibitUrls.map((fileUrl, index) => ({
        id: `${this._periodId}_${productLine.id}_${index}`,
        productLineId: productLine.id,
        fileName: getExhibitFileName(fileUrl),
        fileUrl,
      })),
    );
  }

  public get selectedProductLineId(): string {
    return this.productLineOptions.length === 1 ? this.productLineOptions[0].value : this._selectedProductLineId;
  }

  public get productLineOptions(): OptionType[] {
    return this._productLines.map((productLine) => ({
      value: productLine.id,
      label: PRODUCT_LINES_CONTENT_MAP[productLine.id]?.label ?? productLine.id,
    }));
  }

  public get canSaveFile(): boolean {
    return !this.fileUploadDelegate.noUploadedFiles && !!this.selectedProductLineId;
  }

  public get subtitle(): string {
    if (!this._groupName || !this._effectiveDate) {
      return '';
    }

    return `${this._groupName}, ${normDateToShortAmericanDate(this._effectiveDate)}`;
  }

  public setSelectedProductLineId(productLineId: string): void {
    this._selectedProductLineId = productLineId;
  }

  private _resetUploadState(): void {
    this._selectedProductLineId = '';
    this.fileUploadDelegate.resetFiles();
  }

  public open({ periodId, groupName, effectiveDate, productLines }: ManageExhibitsContext): void {
    this._periodId = periodId;
    this._groupName = groupName;
    this._effectiveDate = effectiveDate;
    this._productLines = productLines;
    this._resetUploadState();
    this.fileUploadDelegate.setIsFileUploadReset(false);
    this._modalDelegate.open();
  }

  public close(): void {
    this._periodId = '';
    this._groupName = '';
    this._effectiveDate = '';
    this._productLines = [];
    this._resetUploadState();
    this._modalDelegate.close();
  }

  public async downloadExhibits(): Promise<void> {
    if (this.allExhibitUrls.length === 0) {
      return;
    }

    toastManager.addToast('Downloading exhibits...', ToastColorTypes.SUCCESS);

    const zipUrl = await getUploadExhibitZipUrl(this.allExhibitUrls);
    if (zipUrl) {
      downloadByUrl(zipUrl);
    }
  }

  public async addEnrollmentExhibits(productLineId: string, fileUrls: string[]): Promise<boolean> {
    return this._mutateExhibits(addEnrollmentExhibits, productLineId, fileUrls, 'Exhibits were successfully added');
  }

  public async removeEnrollmentExhibits(productLineId: string, fileUrls: string[]): Promise<boolean> {
    return this._mutateExhibits(
      removeEnrollmentExhibits,
      productLineId,
      fileUrls,
      'Exhibits were successfully removed',
    );
  }

  public async saveFile(): Promise<void> {
    const uploadedFileNames = this.fileUploadDelegate.uploadedFileNames;
    const productLineId = this.selectedProductLineId;
    if (uploadedFileNames.length === 0 || !productLineId) {
      throw new Error('Tried to save file without a selected product line or uploaded file!');
    }

    this._modalDelegate.setIsLoading(true);

    try {
      const files = toJS(this.fileUploadDelegate.uploadedFiles);
      const uploadResults = await Promise.all(
        uploadedFileNames.map(async (uploadedFileName) => {
          const res = await axios<UploadUrls>({
            url: `https://${COMMON_URLS.api}/carrierFileV2/Prod/upload?fileName=${uploadedFileName}`,
            withCredentials: true,
          });

          return {
            originalFileName: uploadedFileName,
            uploadUrl: Object.values(res.data)[0],
            fileNameFromBE: Object.keys(res.data)[0],
          };
        }),
      );

      const urlsByOriginalName = Object.fromEntries(
        uploadResults.map(({ originalFileName, uploadUrl }) => [originalFileName, uploadUrl]),
      );
      const fileNamesFromBE = uploadResults.map(({ fileNameFromBE }) => fileNameFromBE);

      await uploadFiles(files, urlsByOriginalName);
      const wasSaved = await this.addEnrollmentExhibits(productLineId, fileNamesFromBE);

      if (wasSaved) {
        runInAction(() => {
          this.fileUploadDelegate.resetFiles();
        });
      }
    } catch {
      toastManager.addToast('An error occurred during the file upload process.', ToastColorTypes.WARNING);
      runInAction(() => {
        this._modalDelegate.setIsLoading(false);
      });
    }
  }

  private async _mutateExhibits(
    mutate: (periodId: string, productLineId: string, fileUrls: string[]) => Promise<InstalledEnrollmentType>,
    productLineId: string,
    fileUrls: string[],
    successMessage: string,
  ): Promise<boolean> {
    if (!this._periodId || fileUrls.length === 0) {
      return false;
    }

    this._modalDelegate.setIsLoading(true);

    try {
      const updatedPeriod = await mutate(this._periodId, productLineId, fileUrls);
      runInAction(() => {
        this._productLines = toManageExhibitsProductLines(updatedPeriod);
        this._onEnrollmentPeriodUpdated(updatedPeriod);
      });
      toastManager.addToast(successMessage, ToastColorTypes.SUCCESS);
      return true;
    } catch {
      toastManager.addToast('Something went wrong', ToastColorTypes.WARNING);
      return false;
    } finally {
      runInAction(() => {
        this._modalDelegate.setIsLoading(false);
      });
    }
  }

  constructor({ onEnrollmentPeriodUpdated }: ManageExhibitsModalDelegateParams) {
    this._onEnrollmentPeriodUpdated = onEnrollmentPeriodUpdated;
    makeAutoObservable(this, {}, { autoBind: true });

    reaction(
      () => this.isVisible,
      (isVisible) => {
        runInAction(() => {
          this.fileUploadDelegate.setIsFileUploadReset(!isVisible);
        });
      },
    );
  }
}
